import { isEmpty, isNil, isString, trim } from 'lodash';

const STORAGE_KEY = 'notesDrafts';

export type DraftTarget =
	| 'wo_note:client'
	| 'wo_note:internal'
	| 'wo_note:subcontractor'
	| 'wo_note_reply:client'
	| 'wo_note_reply:internal'
	| 'wo_note_reply:subcontractor';

export interface DraftIdentity {
	target: DraftTarget;
	tenantId: string;
	userId: string;
	entityId: string;
}

export interface DraftManager<TBody> {
	read(identity: DraftIdentity): TBody | null;
	write(identity: DraftIdentity, body: TBody): void;
	clear(identity: DraftIdentity): void;
}

const encodeDraftKeyPart = (value: string | number): string =>
	String(isNil(value) ? '' : value).replace(/\|/g, '_');

const buildEntryKey = (identity: DraftIdentity): string => {
	if (!identity || !identity.target) {
		throw new Error('A draft identity requires a target — see DraftTarget in DraftManager.ts');
	}
	return [identity.target, identity.tenantId, identity.userId, identity.entityId]
		.map(encodeDraftKeyPart)
		.join('|');
};

// Blank drafts are deleted, not stored — otherwise clearing the composer restores old text.
const isBlank = (body: any): boolean => isNil(body) || (isString(body) && isEmpty(trim(body)));

const readStore = (): { [key: string]: any } => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : null;
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch (error) {
		console.warn('Draft read failed:', error);
		return {};
	}
};

const writeStore = (drafts: { [key: string]: any }): void => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
	} catch (error) {
		// A full store must never break typing or submitting.
		console.warn('Draft write failed:', error);
	}
};

/** One manager, shared by every consumer. Uniqueness comes from `target`, not from instances. */
export const draftManager: DraftManager<any> = {
	read: (identity: DraftIdentity): any => {
		const body = readStore()[buildEntryKey(identity)];
		return isNil(body) ? null : body;
	},

	write: (identity: DraftIdentity, body: any): void => {
		const key = buildEntryKey(identity);
		if (isBlank(body)) {
			draftManager.clear(identity);
			return;
		}
		const drafts = readStore();
		drafts[key] = body;
		writeStore(drafts);
	},

	clear: (identity: DraftIdentity): void => {
		const drafts = readStore();
		delete drafts[buildEntryKey(identity)];
		writeStore(drafts);
	},
};
